/**
 * Category Comparison Service
 * Fetches category vs all data for comparison analysis
 */

export interface CategoryYearData {
  funding: number;
  deals: number;
  avgDeal: number;
}

export interface CategoryComparisonData {
  selected: {
    [year: string]: CategoryYearData;
  };
  others: {
    [year: string]: CategoryYearData;
  };
}

export interface CategoryComparisonResponse {
  success: boolean;
  data: CategoryComparisonData;
  category: string;
  type: 'product_category' | 'category_domain';
  years: string[];
  metadata: {
    timeframe: string;
    filters_applied: boolean;
    current_period: {
      start: string;
      end: string;
    };
  };
  cache_status?: string;
  response_time_ms?: number;
}

class CategoryComparisonService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';
  }

  /**
   * Fetch category comparison data
   */
  async getCategoryComparison(
    category: string,
    type: 'product_category' | 'category_domain' = 'product_category',
    timeframe: string = '12w',
    weekOffset: number = 0,
    filters?: Record<string, string[] | undefined>
  ): Promise<CategoryComparisonResponse> {
    const params = new URLSearchParams({
      category,
      type,
      timeframe,
      weekOffset: weekOffset.toString()
    });

    // Add filters to query params
    if (filters) {
      Object.entries(filters).forEach(([key, values]) => {
        if (values && values.length > 0) {
          params.set(key, values.join(','));
        }
      });
    }

    const url = `${this.baseUrl}/api/v2/categories/vs-all?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: CategoryComparisonResponse = await response.json();

      if (!data.success) {
        throw new Error('API returned unsuccessful response');
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch category comparison:', error);
      throw error;
    }
  }
}

export const categoryComparisonService = new CategoryComparisonService();
